/**
 * Pure canvas rendering for the fire animation.
 *
 * Every metric frame is a function of two co-registered rasters:
 *   frame[i] = arrival[i] <= tSec ? metricAtIgnition[i] : transparent
 * and the Confidence frame reads the precomputed per-frame burn-probability.
 *
 * These functions fill a reused `ImageData` (RGBA, size W*H) which the layer
 * then `putImageData`s onto an HTML canvas that Mapbox georeferences to the
 * fire's geographic bounds. No React, no per-frame network.
 */

import type { FrameArrays, ConfidenceArrays, RasterBounds } from "../../../types/FireSimulation";

export type FireView =
  | "rate_of_spread"
  | "fireline_intensity"
  | "flame_height"
  | "isochrones"
  | "confidence";

// Full alpha: the metric layer is composited at raster-opacity 0.85, matching
// the legacy PNG path (which wrote opaque pixels and let the layer dim them).
const FILL_ALPHA = 255;

// Per-metric ramps sampled from the same matplotlib colormaps the legacy PNG
// renderer used (api/services/fire_simulation_raster_images.py):
//   ros -> YlOrRd, intensity -> hot, flame_height -> inferno.
// ROS / YlOrRd (ColorBrewer 6-class, same as the PNG's 'YlOrRd').
const GRADIENT_PALETTE = ["#ffffcc", "#ffeda0", "#feb24c", "#fd8d3c", "#f03b20", "#bd0026"];
// Fireline intensity / matplotlib 'hot' (black -> red -> yellow -> white), 9 even stops.
const HOT_PALETTE = ["#000000", "#570000", "#af0000", "#ff0700", "#ff5a00", "#ffae00", "#ffff04", "#ffff82", "#ffffff"];
// Flame height / matplotlib 'inferno' (perceptually uniform), 11 decile stops.
const INFERNO_PALETTE = ["#000004", "#160b39", "#420a68", "#6a176e", "#932667", "#bc3754", "#dd513a", "#f37819", "#fca50a", "#f6d746", "#fcffa4"];
// Burn-probability ramp for the Confidence heatmap (YlOrRd).
const CONFIDENCE_PALETTE = ["#ffffb2", "#fecc5c", "#fd8d3c", "#f03b20", "#bd0026"];
// Per-hour isochrone colours (cycle), matching the legacy vector style.
const ISOCHRONE_HOUR_COLORS = [
  "#ff595e", "#ff924c", "#ffca3a", "#8ac926", "#52b788", "#1982c4",
  "#6a4c93", "#c77dff", "#ff5d8f", "#ff9770", "#00bbf9", "#00f5d4",
  "#9b5de5", "#f15bb5", "#fee440", "#00b4d8", "#90dbf4", "#5c7aff",
  "#ff6d00", "#ffba08", "#3a86ff", "#8338ec", "#ff006e", "#fb5607",
];

type RGB = [number, number, number];

function hexToRgb(hex: string): RGB {
  const h = hex.replace("#", "");
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

const GRADIENT_RGB = GRADIENT_PALETTE.map(hexToRgb);
const HOT_RGB = HOT_PALETTE.map(hexToRgb);
const INFERNO_RGB = INFERNO_PALETTE.map(hexToRgb);
const CONFIDENCE_RGB = CONFIDENCE_PALETTE.map(hexToRgb);
const ISOCHRONE_RGB = ISOCHRONE_HOUR_COLORS.map(hexToRgb);

/** Interpolate a 0..1 position across an RGB stop list (mirrors getFDIColor). */
function lerpRamp(ramp: RGB[], t: number): RGB {
  if (t <= 0) return ramp[0];
  if (t >= 1) return ramp[ramp.length - 1];
  const seg = t * (ramp.length - 1);
  const i = Math.floor(seg);
  const f = seg - i;
  const a = ramp[i];
  const b = ramp[i + 1];
  return [
    Math.round(a[0] + (b[0] - a[0]) * f),
    Math.round(a[1] + (b[1] - a[1]) * f),
    Math.round(a[2] + (b[2] - a[2]) * f),
  ];
}

function metricColor(value: number, maxVal: number, ramp: RGB[]): RGB {
  const t = maxVal > 0 ? value / maxVal : 0;
  return lerpRamp(ramp, t < 0 ? 0 : t > 1 ? 1 : t);
}

/** Render an ROS/intensity/flame/isochrone frame into `out` (size W*H). */
export function renderMetricFrame(
  out: ImageData,
  view: Exclude<FireView, "confidence">,
  fa: FrameArrays,
  frameIdx: number
): void {
  const [h, w] = fa.shape;
  const n = w * h;
  const data = out.data;
  const arrival = fa.arrival;
  const tSec = fa.frameTimesSeconds[frameIdx] ?? Number.POSITIVE_INFINITY;

  const isIso = view === "isochrones";
  // Per-metric colormap matches the legacy PNG renderer.
  let metric = fa.rosAtIgnition;
  let maxVal = fa.maxRos || 1;
  let ramp = GRADIENT_RGB; // ros -> YlOrRd
  if (view === "fireline_intensity") {
    metric = fa.intensityAtIgnition;
    maxVal = fa.maxIntensity || 1;
    ramp = HOT_RGB; // intensity -> hot
  } else if (view === "flame_height") {
    metric = fa.flameHeightAtIgnition;
    maxVal = fa.maxFlameHeight || 1;
    ramp = INFERNO_RGB; // flame height -> inferno
  }

  for (let i = 0; i < n; i++) {
    const o = i * 4;
    const a = arrival[i];
    if (a <= tSec) {
      let c: RGB;
      if (isIso) {
        const hour = Math.floor(a / 3600);
        c = ISOCHRONE_RGB[((hour % ISOCHRONE_RGB.length) + ISOCHRONE_RGB.length) % ISOCHRONE_RGB.length];
      } else {
        c = metricColor(metric[i], maxVal, ramp);
      }
      data[o] = c[0];
      data[o + 1] = c[1];
      data[o + 2] = c[2];
      data[o + 3] = FILL_ALPHA;
    } else {
      data[o + 3] = 0;
    }
  }
}

/**
 * Render the Confidence (burn-probability) frame: each cell coloured by the
 * fraction of members burnt by this frame, with alpha rising with certainty so
 * faint = uncertain, solid = near-certain.
 */
export function renderConfidenceFrame(
  out: ImageData,
  ca: ConfidenceArrays,
  frameIdx: number
): void {
  const [h, w] = ca.shape;
  const n = w * h;
  const data = out.data;
  const f = Math.max(0, Math.min(frameIdx, ca.frameCount - 1));
  const base = f * n;
  const bp = ca.burnProb;

  for (let i = 0; i < n; i++) {
    const o = i * 4;
    const p = bp[base + i] / 255;
    if (p > 0) {
      const c = lerpRamp(CONFIDENCE_RGB, p);
      data[o] = c[0];
      data[o + 1] = c[1];
      data[o + 2] = c[2];
      data[o + 3] = Math.round((0.2 + 0.65 * p) * 255);
    } else {
      data[o + 3] = 0;
    }
  }
}

/** Bounds → [[lng,lat]...] in Mapbox CanvasSource corner order (TL,TR,BR,BL). */
export function boundsToCoordinates(
  b: RasterBounds
): [[number, number], [number, number], [number, number], [number, number]] {
  return [
    [b.west, b.north],
    [b.east, b.north],
    [b.east, b.south],
    [b.west, b.south],
  ];
}

/** Arrival seconds at a lng/lat (or null if outside the grid). */
export function arrivalAtLngLat(fa: FrameArrays, lng: number, lat: number): number | null {
  const [h, w] = fa.shape;
  const { west, east, north, south } = fa.bounds;
  const col = Math.floor(((lng - west) / (east - west)) * w);
  const row = Math.floor(((north - lat) / (north - south)) * h);
  if (col < 0 || col >= w || row < 0 || row >= h) return null;
  return fa.arrival[row * w + col];
}

/** Has the fire reached (lng,lat) by time tSec? (replaces the canvas pixel probe). */
export function isBurnedAt(fa: FrameArrays, lng: number, lat: number, tSec: number): boolean {
  const a = arrivalAtLngLat(fa, lng, lat);
  return a !== null && a <= tSec;
}

export interface ConfidencePointStats {
  /** Fraction of members that have burnt this cell by the current time (0..1). */
  probNow: number;
  /** Fraction of members that ever burn this cell (by end of simulation). */
  probFinal: number;
  /** Arrival-time percentiles (seconds) derived from the cumulative probability:
   *  the first frame where the burnt fraction reaches 10/50/90%. */
  p10ArrivalSec: number | null;
  p50ArrivalSec: number | null;
  p90ArrivalSec: number | null;
}

/**
 * Across-member statistics at a clicked point, read from the burn-probability
 * stack. `burnProb` is cumulative (monotonic) in frame, so the first frame where
 * the fraction reaches q gives the q-th arrival-time percentile. Returns null if
 * the point is outside the simulation grid; a point inside the grid but outside
 * a cropped stack's window is one no member ever reaches (all zeros).
 */
export function confidencePointStats(
  ca: ConfidenceArrays,
  lng: number,
  lat: number,
  tSec: number
): ConfidencePointStats | null {
  const [h, w] = ca.shape;
  const { west, east, north, south } = ca.bounds;
  const col = Math.floor(((lng - west) / (east - west)) * w);
  const row = Math.floor(((north - lat) / (north - south)) * h);
  if (col < 0 || col >= w || row < 0 || row >= h) {
    const d = ca.domainBounds;
    const inDomain =
      !!d && lng >= d.west && lng <= d.east && lat >= d.south && lat <= d.north;
    return inDomain
      ? { probNow: 0, probFinal: 0, p10ArrivalSec: null, p50ArrivalSec: null, p90ArrivalSec: null }
      : null;
  }
  const p = row * w + col;
  const HW = h * w;
  const F = ca.frameCount;
  const probAt = (f: number) => ca.burnProb[f * HW + p] / 255;

  let fNow = 0;
  for (let f = 0; f < F; f++) {
    if (ca.frameTimesSeconds[f] <= tSec) fNow = f;
    else break;
  }
  const probNow = probAt(fNow);
  const probFinal = probAt(F - 1);

  const firstAtLeast = (q: number): number | null => {
    for (let f = 0; f < F; f++) {
      if (probAt(f) >= q) return ca.frameTimesSeconds[f];
    }
    return null;
  };

  return {
    probNow,
    probFinal,
    p10ArrivalSec: firstAtLeast(0.1),
    p50ArrivalSec: firstAtLeast(0.5),
    p90ArrivalSec: firstAtLeast(0.9),
  };
}

export { GRADIENT_PALETTE, CONFIDENCE_PALETTE, ISOCHRONE_HOUR_COLORS };
